import type { PreviewModifier } from "./index";

function numeric(value: unknown): number | undefined {
    if (typeof value !== "number" && typeof value !== "string")
        return undefined;
    if (typeof value === "string" && !value.trim()) return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
}

function numberModifier(
    apply: (value: number, argument: number) => number,
    fallback?: number,
): PreviewModifier {
    return {
        types: ["int", "float", "number"],
        apply: (value, args) => {
            const number = numeric(value);
            const argument = numeric(args[0]) ?? fallback;
            return number === undefined || argument === undefined
                ? value
                : apply(number, argument);
        },
    };
}

export const numberModifiers: Record<string, PreviewModifier> = {
    add: numberModifier((value, arg) => value + arg),
    subtract: numberModifier((value, arg) => value - arg),
    multiply_by: numberModifier((value, arg) => value * arg),
    divide_by: numberModifier((value, arg) =>
        arg === 0 ? value : value / arg,
    ),
    absolute: numberModifier(Math.abs, 0),
    ceil: numberModifier(Math.ceil, 0),
    floor: numberModifier(Math.floor, 0),
    min: numberModifier(Math.min),
    max: numberModifier(Math.max),
    round: numberModifier((value, precision) => {
        const factor = 10 ** Math.trunc(precision);
        if (!Number.isFinite(factor) || factor === 0) return value;
        const magnitude = Math.abs(value * factor);
        if (!Number.isFinite(magnitude)) return value;
        const rounded =
            (Math.sign(value) *
                Math.round(magnitude + Number.EPSILON * magnitude)) /
            factor;
        return Number.isFinite(rounded) ? rounded : value;
    }, 0),
};
